import { useCallback } from 'react';

import { ensureDefined } from '@e-socna/utils';
import type { ActorListEnum, Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../../../utils/back-errors.util';
import type { BackError } from '../../../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../../../MemoBeforeSubmit/mappers/memo.form2api.mapper';

import type { SubmitHandler } from '../types/submit-handler.type';
import type { RemoveMutation } from '../types/props.type';

import { actorListKeyOfList } from '../utils/actor-list.util';

import { useFindActorIndexById } from './useFindIndexById.hook';

interface Params {
  readonly actorId?: Id | null;
  readonly folderBusinessEntityId: Id;
  readonly list: ActorListEnum;
  readonly directMutation?: RemoveMutation;
  readonly handleRemoveSuccess: () => void;
}

interface FormikArrayActions {
  readonly removeInForm: (index: number) => void;
}

export const useRemoveFolderBusinessEntityActorSubmit = ({
  actorId: pActorId,
  folderBusinessEntityId,
  list,
  directMutation,
  handleRemoveSuccess,
}: Readonly<Params>) => {
  const findIndexById = useFindActorIndexById(actorListKeyOfList(list));

  const handleSubmitFactory = useCallback(
    ({ removeInForm }: Readonly<FormikArrayActions>): SubmitHandler =>
      ({ memos, formikHelpers: { setSubmitting }, setFormError }) => {
        const { actorId } = ensureDefined({ actorId: pActorId });

        setFormError(null);

        const onError = (error: unknown) => {
          setFormError(userFriendlyErrors(error as BackError));
          setSubmitting(false);
        };

        const onSuccess = () => {
          const index = findIndexById(actorId);

          if (index === -1) {
            setFormError(["L'acteur n'existe pas"]);
            setSubmitting(false);
            return;
          }

          removeInForm(index);

          handleRemoveSuccess();
          setSubmitting(false);
        };

        if (directMutation) {
          directMutation(
            {
              actorableId: folderBusinessEntityId,
              list,
              actorId,
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [
      directMutation,
      findIndexById,
      folderBusinessEntityId,
      handleRemoveSuccess,
      pActorId,
      list,
    ],
  );

  return { handleSubmitFactory };
};
